import ArrowRight from "@/assets/icons/ArrowRight";
import Screen from "@/components/layout/Screen";
import AppButton from "@/components/shared/AppButton";
import PageTitle from "@/components/shared/PageTitle/PageTitle";
import { FormControl, FormControlError, FormControlErrorText, FormControlLabel, FormControlLabelText } from "@/components/ui/form-control";
import { Input, InputField } from "@/components/ui/input";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { useAuth } from "@/contexts/AuthContext";
import { useToastNotify } from "@/stores/toastStore";
import { forgotPasswordSchema } from "@/utils/validationSchemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFocusEffect } from "@react-navigation/native";
import * as Sentry from "@sentry/react-native";
import { T, useT } from "@transifex/react";
import { useRouter } from "expo-router";
import { useCallback } from "react";
import { Controller, Form, useForm } from "react-hook-form";
import { Platform, Pressable } from "react-native";
import type { z } from "zod";

enum CognitoErrorMessages {
  CodeDeliveryFailureException = "Failed to send the verification code. Please try again.",
  LimitExceededException = "You have exceeded the allowed number of attempts. Please wait and try again later.",
  TooManyRequestsException = "Too many requests in a short period. Please wait a moment and try again.",
  UserNotFoundException = "User not found. Please check if the email is correct.",
  InvalidParameterException = "Invalid input provided. Please check your information and try again.",
  NotAuthorizedException = "Action not authorized. Please contact support for assistance.",
  ResourceNotFoundException = "Resource not found. Please try again later.",
  InternalErrorException = "An internal error occurred. Please try again later.",
  Default = "An unexpected error occurred. Please try again.",
}

type ForgotPasswordSchemaType = z.infer<typeof forgotPasswordSchema>;

const ForgotPassword = () => {
  const { forgotPassword, isLoading } = useAuth();
  const { notify } = useToastNotify();
  const router = useRouter();
  const t = useT();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ForgotPasswordSchemaType>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const notifyError = (message: string) => notify({ severity: "error", message: t(message) });

  const onSubmit = async (data: ForgotPasswordSchemaType) => {
    try {
      const result = (await forgotPassword(data.email)) as unknown as { error?: { name: string; message?: string } } | null;
      if (result?.error) {
        if (result.error.name === "UserNotFoundException") {
          throw new Error("UserNotFoundException");
        }
        throw new Error(result.error.message ?? "Failed to send reset code.");
      }
      notify({ severity: "success", message: t("Reset code sent successfully. Check your email.") });
      reset({
        email: "",
      });
      router.push("/auth/reset-password");
    } catch (err) {
      if (err instanceof Error && err.name) {
        const errorMessage = CognitoErrorMessages[err.name as keyof typeof CognitoErrorMessages] || CognitoErrorMessages.Default;

        notifyError(errorMessage);
      } else {
        notifyError(t("An error occurred while sending the reset code."));
      }
      Sentry.captureException(err, {
        extra: {
          data,
          localStorage: Platform.OS === "web" ? window.localStorage : null,
        },
      });
    }
  };

  useFocusEffect(
    useCallback(() => {
      reset({ email: "" });
    }, [reset]),
  );

  return (
    <Screen>
      <PageTitle title={t("Forgot Password")} />
      <Form control={control} onSubmit={async () => handleSubmit(onSubmit)()}>
        <VStack className="gap-[32px]">
          <FormControl isDisabled={isLoading} isInvalid={!!errors.email} key="forgot-password" className="gap-[10px]">
            <FormControlLabel>
              <FormControlLabelText className="text-typography-900 text-[14px] leading-[20px] font-semibold align-middle">
                <T _str="Email" />
              </FormControlLabelText>
            </FormControlLabel>
            <Controller
              name="email"
              control={control}
              defaultValue=""
              render={({ field: { onChange, onBlur, value } }) => (
                <Input
                  className={`bg-background-100 h-[52px] pl-[8px] pr-[16px] py-[8px] rounded-[6px]  ${errors.email ? "bg-error-50 border-error-400" : "bg-background-100 border-secondary-700"}`}
                >
                  <InputField
                    placeholder={t("Enter Your Email Address")}
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    returnKeyType="done"
                    className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
                  />
                </Input>
              )}
            />
            <FormControlError>
              <FormControlErrorText className="text-error-100 text-[14px] leading-[20px]">{errors.email?.message ?? null}</FormControlErrorText>
            </FormControlError>
          </FormControl>

          <AppButton title={t("Reset password")} onPress={handleSubmit(onSubmit)} loading={isLoading} icon={ArrowRight} iconPosition="after" />

          <Pressable className="active:opacity-70 inline self-center" onPress={() => router.push("/auth/sign-in")}>
            <Text className="text-[14px] font-[700] leading-[20px] underline text-typography-900">
              <T _str="Back to Sign In." />
            </Text>
          </Pressable>
        </VStack>
        {Platform.OS === "web" && <button className="hidden" type="submit" />}
      </Form>
    </Screen>
  );
};

export default ForgotPassword;
